'use strict';

import React from "react";
import PropTypes from "prop-types";

import {FaTimesCircle, FaExclamationTriangle, FaLink} from "react-icons/fa";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";

import {isEmpty, isFalse, isTrue} from "@uw-it-sis/lib-react/lib/Utils";
import RegistrationCodeLink from "@uw-it-sis/lib-react-myplan/lib/RegistrationCodeLink";
import SectionCode from "@uw-it-sis/lib-react-myplan/lib/SectionCode";
import SectionMeetingDetails from "@uw-it-sis/lib-react-myplan/lib/SectionMeetingDetails";
import Notify from "@uw-it-sis/lib-react-myplan/lib/Notify";
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import Lists from "@uw-it-sis/lib-react/lib/Lists";
import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";
import HelpPopover from "@uw-it-sis/lib-react/lib/HelpPopover";

import errorMessageFactory from "../registration/ErrorMessageFactory";
import PlanItemFunctionConstants from "./PlanItemFunctionConstants";
import {PlanViewType} from "../lib/Constants";

/**
 * Component for displaying a plan item course section
 */
const PlanItemSection = ({planItem, section, viewType = PlanViewType.plan, readOnly = false, sendToRegistration = true}) => {
    if ( isEmpty(section) || section.registrationCode === null ) {
        // This shouldn't happen, but just in case
        return null;
    }

    let courseDetails = planItem.courseDetails;

    let component;

    let slnLink = <RegistrationCodeLink sln={section.registrationCode} qtryr={section.qtryr} className="align-middle" />;

    let blockReg = null;
    // If section is part of a planned block reg (all sections of block must be planned for indicator)
    if (section.plannedBlockRegistration) {
        let blockCoursesTitle = (section.blockRegistration.type.trim() === "FIG" ? "FIG " : "") + "Cluster Registration Section";
        let blockCoursesList = [...section.plannedBlockRegistrationData];
        blockCoursesList.sort(function(a, b) {
            // sort block course codes so that the first one is the current course
            let currentCourse = normalize(section.courseCode);
            if (a.includes(currentCourse)) return -1;
            if (b.includes(currentCourse)) return 1;
            return a - b
        });
        let blockCoursesListEnd = blockCoursesList.pop();

        blockReg =
            <HelpPopover title={blockCoursesTitle} icon={FaLink} placement="top" className="text-muted me-1" flip="horizontal">
                {blockCoursesList.join(", ") + ' and ' + blockCoursesListEnd} are part of a cluster. All cluster sections will be sent to Registration together.
            </HelpPopover>;
    }

    switch (viewType) {
        default:
        case PlanViewType.plan:
            // Plan view display of a section, short summary view ****************************************************************
            component =
                <>
                    {blockReg}
                    <strong className="d-inline-block align-middle" style={{minWidth: "1.75rem"}}>{section.code}</strong>
                    {slnLink}
                </>;
            break;

        case PlanViewType.reg:
            // Reg view display of a section, more detailed display ****************************************************************
            const mode = section.onlineLearningText ? section.onlineLearningText : "In-person";
            // If ready list of courses, build form element for entry code input with value defaulted to store state value (form data is url persisted)
            let entryCode;
            if ( isFalse(readOnly) ) {
                let entryCodeLabel = (section.independentStudy ? "Faculty Code (required)" : "Add Code" + (section.addCodeRequired ? " (required)":""));
                entryCode =
                    <Col xs={12} sm={4}>
                        <Form.Group controlId={"entry-code-" + section.activityId} size="sm" className="mt-2 mt-sm-0 mb-0 mb-sm-3">
                            <Form.Label className="mb-0 mb-sm-1">{entryCodeLabel}</Form.Label>
                            <Form.Control type="number"
                                          value={section.entryCode}
                                          size="sm"
                                          data-activity-id={section.activityId}
                                          disabled={isFalse(sendToRegistration)}
                                          autoComplete="off"
                                          autoCorrect="off"
                                          autoCapitalize="off"
                                          spellCheck="false"
                                          maxLength={5}
                                          pattern="[0-9]*"
                                          onChange={PlanItemFunctionConstants.onFormChange}
                                          onInput={PlanItemFunctionConstants.onEntryCodeInput}
                                          onKeyDown={PlanItemFunctionConstants.onEntryCodeKeyDown} />
                        </Form.Group>
                    </Col>;
            }

            // Instructor display, if empty, show '--'
            let instructor =
                <Col xs={12} sm={8}>
                    Instructor: {isEmpty(section.instructor) ? "--" : section.instructor}
                </Col>;

            // If summer term, create a badge for display
            let summerBadge = null;
            if ( section.hasOwnProperty("summerTerm") && section.summerTerm ) {
                summerBadge =
                    <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border">
                        {section.summerTerm}
                    </Badge>;
            }

            // If section is active, show current enrollment status/count/maximum, else show badge of section state (withdrawn/suspended)
            let enrollment;
            if ( section.stateKey === 'active' && !isEmpty(section.enrollStatus)) {
                let enrollStatus = null;

                const enrollStatusValue = section.enrollStatus.toLowerCase();
                if ( enrollStatusValue === 'open' || enrollStatusValue === 'closed' ) {
                    let enrollBadgeVariant;
                    switch (enrollStatusValue) {
                        case "closed":
                            enrollBadgeVariant = "danger";
                            break;
                        case "open":
                            enrollBadgeVariant = "success";
                            break;
                        default:
                            enrollBadgeVariant = "white";
                    }

                    enrollStatus =
                        <Badge bg={enrollBadgeVariant} className="fs-9 text-uppercase">
                            {enrollStatusValue}
                        </Badge>;
                }

                // Don't show negative numbers for available seats since sometimes counts are larger than maximums
                let availableSeats = Math.max(0, (section.enrollMaximum - section.enrollCount));

                enrollment =
                    <>
                        {enrollStatus}
                        <Badge bg="light-gray" className="text-dark fs-9 text-uppercase fw-normal border">
                            <span>{availableSeats} </span>
                            <span className="sr-only">seats available</span>
                            <abbr title="available" aria-hidden={true}>avail</abbr>
                            <span> of {section.enrollMaximum}{section.enrollEstimate === 'estimate' ? <abbr title="estimated">E</abbr> : ''}</span>
                            <span className="sr-only"> seats</span>
                        </Badge>
                    </>;
            } else {
                enrollment =
                    <a href="https://itconnect.uw.edu/learn/tools/myplan-academic-planner/faqs/" target="_blank">
                        <Badge bg="uw-metallic-gold" className="fs-9 text-uppercase">
                            {section.stateKey}
                        </Badge>
                    </a>;
            }

            let sectionAction;
            // If the section is part of a block reg, do not give delete action
            if ( isFalse(readOnly) ) {
                let hoverText = "Remove section and any planned quizzes, labs, etc. from plan";
                if ( isFalse(section.primary) ) {
                    hoverText = "Remove from plan"
                }

                // create a new hash with the same properties as the activity
                // with a new field with cluster information
                // and a boolean about whether a primary section is planned by itself

                /* FIXME
                let modalActivity = { ...section };

                modalActivity.isClustered = props.isClustered;
                modalActivity.hasPlannedSecondary = props.course.planActivities.filter( a => activity.code === a.primaryActivityOfferingCode && !a.primary ).length > 0;

                let analyticsData = [
                    `hasIssue=${issue}`,
                    `timeSchPublished=${props.isPublished}`,
                    `cCode=${courseCode}`,
                    `sCode=${activity.code}`,
                    'planItemType=section',
                    'context=RegView-link'
                ];
                */

                sectionAction =
                    <ButtonGroup className="ms-auto ps-3">
                        <Notify course={courseDetails} section={section} disabled={appStore.isAdviser()} additionalAnalytics={{context: 'RegView'}} />
                        <Button variant="icon-link"
                                id={"section-remove-" + section.registrationCode}
                                title={hoverText}
                                onClick={() => PlanItemFunctionConstants.onDeleteSection(section, {})}
                                disabled={appStore.isAdviser()}>
                            <FaTimesCircle aria-hidden="true" />
                        </Button>
                    </ButtonGroup>;
            }

            // Check to see if there are any activity level issues
            let activityIssues;
            if ( viewType === PlanViewType.reg && section.validationIssues && !isEmpty(section.validationIssues) ) {
                let analyticsData = {
                    hasIssues: 'true',
                    timeSchPublished: 'true',
                    cCode: normalize(courseDetails.code),
                    sCode: section.code,
                    planItemType: 'section'
                };

                let activityIssuesList = [];
                for ( const errorCode in section.validationIssues ) {
                    activityIssuesList.push(
                        <div key={errorCode}>
                            <Lists data={activity.validationIssues[errorCode]} unstyled />
                            {errorMessageFactory.generateLink(errorCode, planItem, analyticsData)}
                        </div>
                    )
                }

                activityIssues =
                    <div className="mt-2 d-flex align-items-start">
                        <FaExclamationTriangle className="me-2 mt-1 text-warning" aria-hidden={true} />
                        <div>
                            {activityIssuesList}
                        </div>
                    </div>;
            }
            if (isTrue(appStore.getAppAttribute("enable_new_reg_exp"))) {
                entryCode = null;
            }
            component =
                <>
                    <div className="d-flex align-items-start">
                        <div className="sectionCode text-nowrap" style={{width: "47.5px", minWidth: "47.5px"}}>
                            <strong><SectionCode section={section} additionalAnalytics={{context: "PlanView-link"}} /></strong>
                        </div>
                        <Row className="flex-grow-1 d-flex align-items-center g-0">
                            <Col xs={12} sm={8}>
                                {slnLink}
                                <Badge bg="light-gray" className="text-dark align-middle fs-9 text-uppercase fw-normal border ms-2">
                                    {section.activityOfferingType}
                                </Badge>
                                {summerBadge}
                            </Col>
                            <Col xs={12} sm={4} className="text-sm-end">
                                {enrollment}
                            </Col>
                        </Row>
                        {sectionAction}
                    </div>
                    <div style={{paddingLeft: "47.5px", paddingRight: "95px"}}>
                        <SectionMeetingDetails meetings={section.meetingDetailsList} mode={mode} campus={section.campus} />
                        <Row>
                            {instructor}
                            {entryCode}
                        </Row>
                    </div>
                </>;
            break;
    }

    return component;
}

PlanItemSection.propTypes = {
    planItem: PropTypes.object.isRequired,
    section: PropTypes.object.isRequired,
    viewType: PropTypes.oneOf(Object.keys(PlanViewType)).isRequired,
    readOnly: PropTypes.bool,
    sendToRegistration: PropTypes.bool
};

export default PlanItemSection;